import { useCallback, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import styles from './CornerInsight.module.css';

const fmtLapTime = (s) => {
  if (s == null) return '—';
  if (s < 60) return s.toFixed(3);
  const m = Math.floor(s / 60);
  return `${m}:${(s - m * 60).toFixed(3).padStart(6, '0')}`;
};

const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d) ? null : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const M_TO_FT = 3.28084;

// Series colors: "You" wears the Driver 1 accent; the reference is the app's
// standard second series blue.
const OWN_COLOR = '#e8411a';
const REF_COLOR = '#3b82f6';
const REF_NAME  = 'Your best';

// Shapes are stored normalized (speed ÷ approach, long-G ÷ session peak gSum,
// curvature ÷ own peak). The first two de-normalize into real units via the
// per-side scalars the API returns; the line stays a ratio (no natural unit).
const CHARTS = [
  { key: 'speed', title: 'Speed', unit: 'mph', dec: 1,
    scales: e => [e.own_approach_mph, e.ref_approach_mph],
    tip: 'Speed through the corner window (each side scaled by its own approach speed)' },
  { key: 'long', title: 'Braking / drive', unit: 'G', dec: 2,
    scales: e => [e.own_peak_gsum_g, e.ref_peak_gsum_g],
    tip: 'Longitudinal acceleration — braking negative, drive positive' },
  { key: 'curv', title: 'Line', unit: '× peak', dec: 2,
    scales: () => [null, null],
    tip: 'Signed path curvature relative to its own peak — the shape of the line' },
];

function ShapeOverlay({ title, tip, unit, dec, own, ref_, ownScale, refScale,
                        onHoverPct, onHoverEnd, onOpen }) {
  if (!own || !ref_) return null;
  const scaled = unit !== '× peak' && ownScale != null && refScale != null;
  const rows = own.map((v, i) => ({
    pct: Math.round((i / (own.length - 1)) * 100),
    You: scaled ? v * ownScale : v,
    [REF_NAME]: scaled ? ref_[i] * refScale : ref_[i],
  }));
  const unitLabel = scaled || unit === '× peak' ? unit : '× approach';
  return (
    <div className={styles.chart} title={tip}
         style={onOpen ? { cursor: 'pointer' } : undefined}>
      <div className={styles.chartTitle}>{title} <span className={styles.chartUnit}>({unitLabel})</span></div>
      <ResponsiveContainer width="100%" height={140}>
        <LineChart data={rows} accessibilityLayer={false}
                   margin={{ top: 4, right: 8, bottom: 0, left: 0 }}
                   onMouseMove={s => { if (s?.activeLabel != null) onHoverPct?.(s.activeLabel); }}
                   onMouseLeave={onHoverEnd}
                   onClick={onOpen}>
          <CartesianGrid stroke="#2c2c2c" strokeDasharray="3 3" />
          <XAxis dataKey="pct" tick={{ fill: '#787878', fontSize: 10 }}
                 tickFormatter={v => `${v}%`} />
          <YAxis tick={{ fill: '#787878', fontSize: 10 }} width={44}
                 domain={['auto', 'auto']}
                 tickFormatter={v => Number(v).toFixed(dec > 1 ? 1 : 0)} />
          <Tooltip
            contentStyle={{ background: '#111', border: '1px solid #2c2c2c', fontSize: 12 }}
            labelFormatter={v => `${v}% through corner`}
            formatter={v => (v == null ? '—' : `${v.toFixed(dec)} ${unitLabel}`)}
          />
          <Line type="monotone" dataKey="You" stroke={OWN_COLOR}
                strokeWidth={2} dot={false} isAnimationActive={false} />
          <Line type="monotone" dataKey={REF_NAME} stroke={REF_COLOR}
                strokeWidth={2} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Per-corner insight section inside an expanded Corner Metrics card: the
 * session's own centroid shapes overlaid with the centroid of the driver's own
 * faster passes at this corner ("your best vs your typical"), plus the
 * apex-speed gap and technique phrases. Self-comparison only for now — the
 * backend's cross-driver "field" mode stays available for a future iteration
 * (same-car matching proved too narrow: one driver per car name in practice).
 * Data arrives via MetricsPanel's per-session cache.
 *
 * Chart hover republishes the cross-panel cursor (`trackapp:chart-hover`) so
 * the track map and traces follow. The 0–100% window axis is mapped onto lap
 * distance via the same soft corner span the card's hover band uses
 * (apex − entry span → apex + exit span) — approximate by design, like the band.
 */
export default function CornerInsight({ cornerId, insight,
                                        corner, apexDistM, tracePoints,
                                        currentSessionId, loadedSessionIds = [],
                                        onLoadSession, canAddMore = true }) {
  const data = insight?.self;                 // null = not fetched yet
  const entry = data?.corners?.[cornerId];
  const [showLoad, setShowLoad] = useState(false);

  const bp = entry?.best_pass;
  const bpIsCurrent = bp && bp.session_id === currentSessionId;
  const bpIsLoaded  = bp && !bpIsCurrent && loadedSessionIds.includes(bp.session_id);
  const bpName = bp ? (bp.session_label || fmtDate(bp.session_date) || 'that session') : null;

  const handleHoverPct = useCallback((pct) => {
    if (!tracePoints?.length || apexDistM == null) return;
    const eSpan = corner?.entry_dist_m > 0 ? corner.entry_dist_m : 100;
    const xSpan = corner?.exit_dist_m > 0 ? corner.exit_dist_m : 100;
    const distM = apexDistM - eSpan + (pct / 100) * (eSpan + xSpan);
    let best = null, bestD = Infinity;
    for (const p of tracePoints) {
      const d = Math.abs(p.dist_m - distM);
      if (d < bestD) { bestD = d; best = p; }
    }
    if (best?.lat != null && best?.lon != null) {
      window.dispatchEvent(new CustomEvent('trackapp:chart-hover', {
        detail: { lat: best.lat, lon: best.lon, distFt: distM * M_TO_FT, videoTime: null },
      }));
    }
  }, [tracePoints, apexDistM, corner]);

  const handleHoverEnd = useCallback(() => {
    window.dispatchEvent(new CustomEvent('trackapp:chart-hover', { detail: null }));
  }, []);

  return (
    <div className={styles.section}>
      <div className={styles.head}>
        <span className={styles.title}
              title="Your typical pass at this corner vs your own faster passes, from any of your sessions">
          Corner Insight — vs my best
        </span>
      </div>

      {data == null && <div className={styles.empty}>Loading…</div>}

      {data != null && !entry && (
        <div className={styles.empty}>
          No meaningfully faster passes of your own found for this corner.
        </div>
      )}

      {entry && (
        <>
          <div className={styles.summary}>
            <span className={styles.gap}>
              {entry.own_mph} → {entry.ref_mph} mph
              <span className={styles.gapDelta}> (+{entry.gap_mph})</span>
            </span>
            <span className={styles.counts}>
              min speed, median of {entry.n_neighbors} passes
              {` · ${entry.n_sessions} of your sessions`}
            </span>
          </div>

          {entry.how?.length > 0 && (
            <ul className={styles.how}>
              {entry.how.map(h => <li key={h}>{h}</li>)}
            </ul>
          )}

          <div className={styles.legend}>
            <span><i style={{ background: OWN_COLOR }} /> You (typical)</span>
            <span><i style={{ background: REF_COLOR }} /> {REF_NAME}</span>
          </div>
          <div className={styles.charts}>
            {CHARTS.map(c => {
              const [ownScale, refScale] = c.scales(entry);
              return (
                <ShapeOverlay key={c.key} title={c.title} tip={c.tip}
                              unit={c.unit} dec={c.dec}
                              own={entry.shapes?.own?.[c.key]}
                              ref_={entry.shapes?.neighbor?.[c.key]}
                              ownScale={ownScale} refScale={refScale}
                              onHoverPct={handleHoverPct}
                              onHoverEnd={handleHoverEnd}
                              onOpen={bp ? () => setShowLoad(true) : null} />
              );
            })}
          </div>

          {showLoad && bp && (
            <div className={styles.loadOverlay} onClick={() => setShowLoad(false)}>
              <div className={styles.loadDialog} onClick={e => e.stopPropagation()}>
                <div className={styles.loadTitle}>Your best pass here</div>
                <div className={styles.loadBody}>
                  {bpName}
                  {bp.lap_number != null && <> · lap {bp.lap_number}</>}
                  {bp.lap_time_s != null && <> · {fmtLapTime(bp.lap_time_s)}</>}
                  {bp.vmin_mph != null && <> · {bp.vmin_mph} mph here</>}
                </div>
                {bpIsCurrent && (
                  <div className={styles.loadNote}>That pass is in this session.</div>
                )}
                {bpIsLoaded && (
                  <div className={styles.loadNote}>That session is already loaded for comparison.</div>
                )}
                {!bpIsCurrent && !bpIsLoaded && onLoadSession && !canAddMore && (
                  <div className={styles.loadNote}>All comparison slots are full — remove one to load it.</div>
                )}
                <div className={styles.loadActions}>
                  {!bpIsCurrent && !bpIsLoaded && onLoadSession && canAddMore && (
                    <button className={styles.loadBtn}
                            onClick={() => { setShowLoad(false); onLoadSession(bp.session_id); }}>
                      Load for comparison
                    </button>
                  )}
                  <button className={styles.cancelBtn} onClick={() => setShowLoad(false)}>
                    {bpIsCurrent || bpIsLoaded || !onLoadSession || !canAddMore ? 'Close' : 'Cancel'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
